<template>
    <div
        class="relative flex flex-col items-center p-3 font-sans bg-stack-page-bg w-80vw sm:w-50vw md:w-40vw lg:w-30vw"
        :style="cssVars"
    >
        <div
            class="w-full rounded-t-3xl bg-stack-background px-3 pt-10 pb-16 shadow-2xl"
        >
            <!-- Perfil -->
            <div class="flex flex-col items-center space-y-3 pt-2 text-center">
                <div
                    class="h-24 w-24 flex-shrink-0 overflow-hidden rounded-full bg-theme-hover flex items-center justify-center"
                >
                    <img
                        v-if="logoIconUrl"
                        :src="logoIconUrl"
                        alt="Logo del negocio"
                        class="h-full w-full object-cover"
                    />
                    <!-- Mismo respaldo que el catálogo (LogoInitial): la
                         inicial del negocio sobre el color de énfasis. -->
                    <div
                        v-else
                        class="flex h-full w-full select-none items-center justify-center bg-stack-emphasis text-4xl font-semibold uppercase leading-none text-stack-ink"
                        aria-hidden="true"
                    >
                        {{ businessInitial }}
                    </div>
                </div>

                <div class="flex flex-col items-center space-y-1">
                    <div
                        class="text-lg font-semibold tracking-normal text-stack-primary"
                    >
                        {{ businessName }}
                    </div>

                    <div
                        v-if="businessDescription"
                        class="max-w-xs text-xs leading-relaxed text-stack-text"
                    >
                        {{ businessDescription }}
                    </div>
                </div>
            </div>

            <!-- Redes sociales -->
            <div
                v-if="socialEntries.length"
                class="flex flex-wrap justify-center gap-x-6 gap-y-3 pb-6 pt-5"
            >
                <a
                    v-for="social in socialEntries"
                    :key="social.network"
                    :href="social.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="transition-opacity hover:opacity-70 text-stack-emphasis"
                >
                    <SocialIcon
                        :network="social.network"
                        :height="8"
                        :width="8"
                    />
                </a>
            </div>

            <!-- Links -->
            <!-- Tabs Home/Tienda (modo mix) -->
            <div
                v-if="showMixTabs"
                class="mx-auto mb-3 mt-2 flex w-full items-center rounded-lg border border-stack-text bg-stack-background p-1"
            >
                <button
                    type="button"
                    class="flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition"
                    :class="
                        editorTab === 'home'
                            ? 'bg-stack-emphasis text-stack-ink'
                            : 'text-stack-text'
                    "
                    @click="editorTab = 'home'"
                >
                    Home
                </button>
                <button
                    type="button"
                    class="flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition"
                    :class="
                        editorTab === 'store'
                            ? 'bg-stack-emphasis text-stack-ink'
                            : 'text-stack-text'
                    "
                    @click="editorTab = 'store'"
                >
                    Tienda
                </button>
            </div>

            <!-- Bloques -->
            <div
                v-if="showSegmentsSection && segments.length"
                class="space-y-4 pb-5"
                :class="{
                    'pt-5': !socialEntries.length
                }"
            >
                <SegmentPreview
                    v-for="segment in segments"
                    :key="segment.id"
                    :segment="segment"
                    :stack="preview.stack"
                    :dark="preview.dark"
                    :is-desktop="false"
                    :padded="false"
                    interactive
                />
            </div>

            <template v-if="showProductsSection">
                <!-- Encabezado productos -->
                <div
                    class="flex h-10 items-center justify-center gap-2 border-t border-stack-text text-xs font-semibold uppercase tracking-widest text-stack-text"
                >
                    <ProductsGridIcon class="w-3.5 h-3.5" />
                    <span>Productos</span>
                </div>

                <!-- Vacío -->
                <div
                    v-if="!products.length"
                    class="flex flex-col items-center space-y-2 px-6 pb-16 pt-12 text-center"
                >
                    <span
                        class="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-stack-text text-stack-emphasis"
                    >
                        <ShoppingBagIcon class="w-6 h-6" />
                    </span>

                    <div class="text-base font-semibold text-stack-primary">
                        Aún no hay productos publicados
                    </div>

                    <div
                        class="max-w-xs text-xs leading-relaxed text-stack-text"
                    >
                        Cuando agregues tus productos, aparecerán aquí.
                    </div>
                </div>

                <!-- Productos -->
                <div v-else class="grid grid-cols-2 gap-3 pb-4">
                    <component
                        :is="productItemCardComponent"
                        v-for="p in products"
                        :key="p.slug"
                        :product="p"
                        open-in-new-tab
                    />
                </div>
            </template>

            <CatalogWatermark v-if="catalog.showWatermark" />
        </div>

        <!-- Botón WhatsApp -->
        <a
            v-if="whatsappBase"
            :href="whatsappLink"
            target="_blank"
            rel="noopener noreferrer"
            class="absolute right-6 bottom-6 z-10 w-14 h-14 rounded-full bg-theme-success flex items-center justify-center shadow-xl text-white"
        >
            <SocialIcon network="whatsapp" />
        </a>
    </div>
</template>

<script>
import { buildCatalogPreviewViewModel } from "@/common/lib/catalog-preview";
import { buildWhatsappWaMeLink } from "@/common/lib/whatsapp";

import CatalogWatermark from "@/components/catalog/CatalogWatermark.vue";
import {
    DEFAULT_PRODUCT_CAROUSEL_VARIANT,
    productCardComponents
} from "@/components/catalog/product-sliders";
import SegmentPreview from "@/components/editor/SegmentPreview.vue";
import ProductsGridIcon from "@/components/icons/ProductsGridIcon.vue";
import ShoppingBagIcon from "@/components/icons/ShoppingBagIcon.vue";
import SocialIcon from "@/components/icons/SocialIcon.vue";

export default {
    name: "CatalogPreviewFrame",
    components: {
        SocialIcon,
        ProductsGridIcon,
        ShoppingBagIcon,
        CatalogWatermark,
        SegmentPreview
    },
    props: {
        catalog: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            editorTab: "home"
        };
    },
    computed: {
        preview() {
            return buildCatalogPreviewViewModel(this.catalog);
        },
        logoIconUrl() {
            return this.preview.logoIconUrl;
        },
        businessName() {
            return this.preview.businessName;
        },
        // `Array.from` para no partir un emoji o una letra compuesta.
        businessInitial() {
            return Array.from((this.businessName || "").trim())[0] || "";
        },
        businessDescription() {
            return this.preview.businessDescription;
        },
        whatsappNumber() {
            return this.catalog?.whatsappNumber ?? "";
        },
        socialEntries() {
            return this.preview.socialEntries;
        },
        cssVars() {
            return this.preview.cssVars;
        },
        products() {
            return this.preview.products;
        },
        productItemCardComponent() {
            return (
                productCardComponents[this.catalog.productItemDesign] ||
                productCardComponents[DEFAULT_PRODUCT_CAROUSEL_VARIANT]
            );
        },
        segments() {
            return this.preview.segments;
        },
        displayMode() {
            return this.preview.displayMode;
        },
        showProductsSection() {
            return (
                this.displayMode === "catalog" ||
                (this.displayMode === "mix" && this.editorTab === "store")
            );
        },
        showSegmentsSection() {
            return this.displayMode !== "mix" || this.editorTab === "home";
        },
        showMixTabs() {
            return this.displayMode === "mix";
        },
        whatsappBase() {
            const number = this.whatsappNumber;
            if (number) return buildWhatsappWaMeLink(number);
            return (
                (this.catalog?.socialNetworks ?? []).find(
                    (entry) => entry.network === "whatsapp"
                )?.url ?? ""
            );
        },
        whatsappLink() {
            if (!this.whatsappBase) return "";
            const message = `Hola ${this.businessName}! Vi tu catálogo y quiero más información.`;
            const separator = this.whatsappBase.includes("?") ? "&" : "?";
            return `${this.whatsappBase}${separator}text=${encodeURIComponent(
                message
            )}`;
        }
    }
};
</script>
